import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import ServiceManager from './base/service-manager/service-manager';
import { guid } from '../util';
import ToastQueue from './toast.queue';
import ToastContainer from './toast-group.container';
import { ToastStatus } from './toast.schema';

class ToastManager extends ServiceManager {
  static propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
  };

  static DefaultDuration = 5000;

  static DefaultClosable = true;

  static DefaultPauseable = true;

  constructor(queue = new ToastQueue()) {
    super(queue, 'span');
    this.containerRef = createRef();
    this.initialCommand = [];
    this.queue.subscribe(this.render);
  }

  render = queue => {
    this.flush(
      <ToastContainer
        ref={this.containerRef}
        onClose={this.handleClose}
        toasts={queue}
        placement="bottomLeft"
        className={this.className}
        tmpl={this.tmpl}
      />,
      this.flushCallback,
    );
  };

  flushCallback = () => {
    if (this.initialCommand.length !== 0) {
      this.initialCommand.forEach(cmd => cmd());
      this.initialCommand.length = 0;
    }
  };

  createToastInstance = props => {
    const {
      key,
      duration = ToastManager.DefaultDuration,
      pauseable = ToastManager.DefaultPauseable,
    } = props;

    const { clearTimeout, startTimeout } = this.createSlice(key, duration);

    const nextProps = {
      closeable: ToastManager.DefaultClosable,
      ...props,
      clearTimeout,
      duration,
      pauseable,
      startTimeout,
    };

    this.queue.add(nextProps);
    return key;
  };

  error(props) {
    return this.createToastInstance({
      ...props,
      key: guid(),
      status: ToastStatus.Error,
    });
  }

  success(props) {
    return this.createToastInstance({
      ...props,
      key: guid(),
      status: ToastStatus.Success,
    });
  }

  warning(props) {
    return this.createToastInstance({
      ...props,
      key: guid(),
      status: ToastStatus.Warning,
    });
  }

  info(props) {
    return this.createToastInstance({
      ...props,
      key: guid(),
      status: ToastStatus.Info,
    });
  }

  closeAll() {
    this.queue.deleteAll();
  }

  getDuplicationKey(props) {
    return this.queue.getDuplicationKey(props);
  }

  setTemplate(tmpl) {
    if (this.containerRef.current) {
      this.containerRef.current.replaceTemplate(tmpl);
    } else {
      this.initialCommand.push(() => this.containerRef.current.replaceTemplate(tmpl));
    }
    this.tmpl = tmpl;
  }
}

export default ToastManager;
